import { useQuery } from "@tanstack/react-query";

import { fetchConfiguredClients, fetchNasActivity } from "@/api/endpoints/nas";

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

export function NasPage() {
  const activityQuery = useQuery({ queryKey: ["nas", "activity"], queryFn: fetchNasActivity });
  const configuredQuery = useQuery({ queryKey: ["nas", "configured"], queryFn: fetchConfiguredClients });

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="mb-3 font-mono text-xs uppercase tracking-wide text-text-muted">
          Access points seen ({activityQuery.data?.total ?? "…"})
        </h2>
        <p className="mb-3 text-xs text-text-muted">
          Individual AP IPs from live accounting data — more useful here than the configured
          client list below, since all APs share one subnet-wide RADIUS client entry rather than
          being registered individually.
        </p>
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3">NAS IP</th>
                <th className="px-4 py-3">Unique users</th>
                <th className="px-4 py-3">Sessions</th>
                <th className="px-4 py-3">First seen</th>
                <th className="px-4 py-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {activityQuery.data?.items.map((n) => (
                <tr key={n.nasipaddress} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-mono text-text">{n.nasipaddress}</td>
                  <td className="px-4 py-3 tabular-nums text-text-muted">{n.unique_users}</td>
                  <td className="px-4 py-3 tabular-nums text-text-muted">{n.session_count}</td>
                  <td className="px-4 py-3 text-text-muted">{formatDate(n.first_seen)}</td>
                  <td className="px-4 py-3 text-text-muted">{formatDate(n.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-3 font-mono text-xs uppercase tracking-wide text-text-muted">
          Configured RADIUS clients
        </h2>
        <p className="mb-3 text-xs text-text-muted">
          Parsed from <code className="font-mono">{configuredQuery.data?.source_path}</code> —
          read-only, shared secrets are never parsed or exposed here.
        </p>
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3">Shortname</th>
              </tr>
            </thead>
            <tbody>
              {configuredQuery.data?.items.map((c) => (
                <tr key={c.name} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-mono text-text">{c.name}</td>
                  <td className="px-4 py-3 font-mono text-text-muted">{c.address ?? "—"}</td>
                  <td className="px-4 py-3 text-text-muted">{c.shortname ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
